import { BoardNote } from "@/bryntum-lib/models/BoardNote";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Dialog, DialogTrigger } from "@/modern/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Copy, EditIcon, Eye, EyeIcon, Link, NotebookIcon, PlusIcon, Save, Trash2, XCircle } from "lucide-react";
import { useMemo } from "react";
import { useNotesMenu } from "@/react/hooks/entityHooks/useNotesMenu";
import { MenuItem, MoreMenu, SharedModalFrostedGlass } from "@/modern/components/more-menu";
import { useEntityNotesContext, useEntityNotesContextOptional } from "@/react/components/entityViews/notes/context/EntityNotesContext";
import { EntityRecord } from "@/types/entity";
import { useNotesModal } from "@/react/hooks/entityHooks/useNotesModal";

interface NotesMenuProps {
  noteRecord: BoardNote | null;
  isEditor?: boolean;
  onDeleteNote?: () => void;
  onOpenNewNote?: () => void;
  record?: EntityRecord; // Optional override for when used outside context
}

type NotesButtonGroupProps = NotesMenuProps & {
  activeNote: BoardNote | null;
  readOnly: boolean;
  setReadonly: (readOnly: boolean) => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  hasChanges: boolean;
};

export const NotesButtonGroup = ({
  noteRecord,
  isEditor = false,
  onDeleteNote,
  onOpenNewNote,
  activeNote,
  readOnly,
  setReadonly,
  handleSave,
  handleCancel,
  hasChanges,
  record
}: NotesButtonGroupProps) => {
  const context = useEntityNotesContextOptional();
  const entityRecord = record || context?.entityRecord;

  if (!entityRecord) {
    throw new Error("NotesButtonGroup requires either a record prop or EntityNotesProvider");
  }
  const {
    loading,
    canEdit,
    NewNoteEditor,
    OpenNoteEditor,
    handleCreateNewNote,
    isModalOpen,
    activeModal,
    showUnsavedDialog,
    setKeepModalOpen,
    setShowUnsavedDialog,
    handleModalChange,
    openNewNoteModal,
    openEditNoteModal,
    closeModal
  } = useNotesModal({
    record: entityRecord,
    noteRecord,
    isEditor,
    onDeleteNote,
    onOpenNewNote
  });

  const ModalComponents = {
    newNote: { component: NewNoteEditor, title: "New Note" },
    openNote: { component: OpenNoteEditor, title: "Edit Note" }
  };
  const ModalComponent = ModalComponents[activeModal || "newNote"];

  return (
    <>
      <Dialog modal={true} open={isModalOpen} onOpenChange={handleModalChange}>
        <ButtonGroup>
          {activeNote && (
            <>
              {readOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="entityHeader" size="icon" onClick={() => setReadonly(false)} disabled={loading || !canEdit}>
                      <EditIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit note inline</TooltipContent>
                </Tooltip>
              ) : (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="entityHeader" size="icon" onClick={handleSave} disabled={!hasChanges || loading || !canEdit}>
                        <Save />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Save note changes</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="entityHeader" size="icon" onClick={handleCancel} disabled={loading}>
                        <XCircle />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Cancel changes</TooltipContent>
                  </Tooltip>
                </>
              )}
            </>
          )}
          {activeNote && (
            <Tooltip>
              <TooltipTrigger asChild>
                <DialogTrigger
                  asChild
                  onClick={() => {
                    onOpenNewNote?.();
                    openEditNoteModal();
                  }}
                >
                  <Button variant="entityHeader" size="icon">
                    <EyeIcon />
                  </Button>
                </DialogTrigger>
              </TooltipTrigger>
              <TooltipContent>Open note</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger
                asChild
                onClick={() => {
                  handleCreateNewNote();
                  openNewNoteModal();
                }}
              >
                <Button variant="entityHeader" size="icon">
                  <PlusIcon />
                </Button>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent>New note</TooltipContent>
          </Tooltip>
        </ButtonGroup>
        <SharedModalFrostedGlass className=" w-full md:max-w-screen-md  min-h-0 flex flex-col flex-1">
          {activeModal && (
            <ModalComponent.component showUnsavedDialog={showUnsavedDialog} setShowUnsavedDialog={setShowUnsavedDialog} isDirty={setKeepModalOpen} onDeleteNote={onDeleteNote} onFinish={closeModal} />
          )}
        </SharedModalFrostedGlass>
      </Dialog>
    </>
  );
};

export const NotesMenu = ({ noteRecord, isEditor = false, onDeleteNote, onOpenNewNote, record }: NotesMenuProps) => {
  const context = useEntityNotesContext();
  const entityRecord = record || context.entityRecord;
  const { loading, canEdit, NewNoteEditor, OpenNoteEditor, handleCreateNewNote, handleDeleteNote, handleCopyLink, handleDuplicateNote } = useNotesMenu({
    record: entityRecord,
    noteRecord,
    isEditor,
    onDeleteNote,
    onOpenNewNote
  });

  // Memoize the menu items array to prevent recreation
  const menuItems = useMemo(
    (): MenuItem[] => [
      ...(!isEditor
        ? [
            {
              itemVariant: "modal" as const,
              variant: "header" as const,
              menuText: "New Note",
              Icon: NotebookIcon,
              classNameContent: "flex overflow-hidden min-h-0 flex-1",
              modalClassName: "overflow-hidden min-h-0 flex flex-col flex-1",
              dropdownMenuItemProps: {
                disabled: loading,
                onClick: handleCreateNewNote
              },
              Component: NewNoteEditor,
              ModalHeader: () => <></>,
              menuItemModalHeaderVariant: "header" as const,
              noWrapper: true
            },
            {
              itemVariant: "separator" as const
            },
            {
              itemVariant: "modal" as const,
              variant: "header" as const,
              menuText: "Open Note",
              Icon: Eye,
              classNameContent: "flex overflow-hidden min-h-0 flex-1",
              modalClassName: "overflow-hidden min-h-0 flex flex-col flex-1",
              dropdownMenuItemProps: {
                disabled: !noteRecord || loading,
                onClick: () => {
                  onOpenNewNote?.();
                }
              },
              Component: OpenNoteEditor,
              ModalHeader: () => <></>,
              menuItemModalHeaderVariant: "header" as const,
              noWrapper: true
            }
          ]
        : []),

      {
        itemVariant: "button",
        menuText: "Copy Link",
        dropdownMenuItemProps: {
          disabled: !noteRecord || loading,
          onClick: handleCopyLink
        },
        Component: () => (
          <div className="flex items-center gap-3">
            <Link className="h-4 w-4" />
            Copy Link
          </div>
        )
      },
      {
        itemVariant: "button",
        menuText: "Duplicate Note",
        dropdownMenuItemProps: {
          disabled: !noteRecord || loading || !canEdit,
          onClick: handleDuplicateNote
        },
        Component: () => (
          <div className="flex items-center gap-3">
            <Copy className="h-4 w-4" />
            Duplicate Note
          </div>
        )
      },
      {
        itemVariant: "separator" as const
      },
      {
        itemVariant: "button",
        menuText: "Delete Note",
        dropdownMenuItemProps: {
          disabled: !noteRecord || loading || !canEdit,
          className: "!text-destructive",
          onClick: handleDeleteNote
        },
        Component: () => (
          <div className="flex items-center gap-3">
            <Trash2 className="h-4 w-4" />
            Delete Note
          </div>
        )
      }
    ],
    [isEditor, loading, noteRecord, canEdit, handleCreateNewNote, handleDeleteNote, handleCopyLink, handleDuplicateNote, NewNoteEditor, OpenNoteEditor]
  );

  return <MoreMenu record={entityRecord} disabled={noteRecord?.isPhantom} menuItems={menuItems} triggerButtonVariant={"entityHeader"} triggerButtonSize="icon-sm" />;
};
